import { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import {
  dealsAPI,
  stagesAPI,
  companiesAPI,
  contactsAPI,
  appointmentsAPI,
} from "../services/api";
import { getUserInfo, getDatePresetRange } from "../utils/helpers";
import { VIEW_TYPES } from "../utils/constants";

// Leads loaded per board column at a time (first load and each "load more")
const BOARD_PAGE_SIZE = 25;
const LIST_ROWS_OPTIONS = [10, 25, 50, 100];
const DEFAULT_LIST_ROWS = 25;

// Pipeline filters are persisted so they survive leaving the board — opening a
// single lead and coming back, or a page reload. They are only reset by the
// "Clear" button in LeadFilters. Scoped per user and per pipeline so switching
// accounts or pipelines doesn't inherit someone else's filters.
const filtersStorageKey = (userId, pipeline) =>
  `leadPipelineFilters:${userId || "anon"}:${pipeline}`;

const readStoredFilters = (userId, pipeline) => {
  try {
    const raw = localStorage.getItem(filtersStorageKey(userId, pipeline));
    // The app wraps localStorage (services/localStorageSecurity), whose getItem
    // returns the decrypted value already JSON-parsed — accept both shapes.
    // JSON.parse on that object threw, and the {} fallback then overwrote the
    // saved filters on every page load.
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    // Re-resolve "Today"/"Yesterday" against the current date
    const presetRange = getDatePresetRange(parsed.datePreset);
    return presetRange ? { ...parsed, ...presetRange } : parsed;
  } catch (error) {
    // Storage unavailable or corrupt — fall back to no filters
    return {};
  }
};

// `loadDeals: false` skips fetching the lead list, for pages that only need
// stages and deal operations (e.g. the single-lead view).
export const useLeadData = ({ view = VIEW_TYPES.BOARD, loadDeals = true } = {}) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlPipeline = queryParams.get("pipeline") || "meta";

  const [pipeline, setPipeline] = useState(urlPipeline);

  useEffect(() => {
    setPipeline(urlPipeline);
  }, [urlPipeline]);

  const [stages, setStages] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const { userId, userName } = getUserInfo();
  const ownerName = userName || localStorage.getItem("companyName") || localStorage.getItem("emailNo") || "";

  // Restored from storage so a filter set before opening a lead is still
  // applied when the user navigates back.
  const [filters, setFilters] = useState(() =>
    readStoredFilters(userId, urlPipeline)
  );

  // Swap in the saved filters for whichever pipeline is active. The identity
  // check keeps this from firing a redundant re-fetch on mount.
  useEffect(() => {
    const stored = readStoredFilters(userId, pipeline);
    setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(stored) ? prev : stored
    );
  }, [userId, pipeline]);

  // Persist every change, including the Clear button's reset to {}.
  useEffect(() => {
    try {
      localStorage.setItem(
        filtersStorageKey(userId, pipeline),
        JSON.stringify(filters)
      );
    } catch (error) {
      // Storage unavailable — filters simply won't survive navigation
    }
  }, [filters, userId, pipeline]);


  // Fetch Stages
  useEffect(() => {
    const fetchStages = async () => {
      try {
        const res = await stagesAPI.getAll(userId, { pipeline });
        setStages(res.data);
      } catch (error) {
        console.error("Error fetching stages:", error);
      }
    };
    if (userId) fetchStages();
  }, [userId, pipeline]);

  // Fetch Companies
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await companiesAPI.getAll(userId);
        setCompanies(res.data);
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    if (userId) fetchCompanies();
  }, [userId]);

  // Fetch Contacts
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        const res = await contactsAPI.getAll(userId);
        setContacts(res.data);
      } catch (error) {
        console.error("Error fetching contacts:", error);
      }
    };
    if (userId) fetchContacts();
  }, [userId]);

  // Fetch Appointments
  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const res = await appointmentsAPI.getAll({ userId });
        setAppointments(res.validatedAppointments || res.data.appointments || []);
      } catch (error) {
        console.error("Error fetching appointments:", error);
      }
    };
    if (userId) fetchAppointments();
  }, [userId]);

  // ---- Deals (server-side paginated) ----
  // Board: each stage column holds the first page of its leads and loads more on
  // demand. List: one server page at a time. Appointments: only leads with a
  // follow-up. Export fetches every matching lead separately (fetchAllDeals).
  const [board, setBoard] = useState({ total: 0, columns: {} });
  const [list, setList] = useState({ deals: [], total: 0 });
  const [followUps, setFollowUps] = useState([]);

  // List page and page size live in the URL (?page=&rows=) so opening a lead
  // and coming back returns to the same page
  const [searchParams, setSearchParams] = useSearchParams();
  const listPage = Math.max(parseInt(searchParams.get("page"), 10) || 1, 1) - 1;
  const listRowsPerPage = LIST_ROWS_OPTIONS.includes(Number(searchParams.get("rows")))
    ? Number(searchParams.get("rows"))
    : DEFAULT_LIST_ROWS;
  const setListParams = (page, rows) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (page > 0) next.set("page", page + 1);
        else next.delete("page");
        if (rows !== DEFAULT_LIST_ROWS) next.set("rows", rows);
        else next.delete("rows");
        return next;
      },
      { replace: true }
    );

  // Latest filters for the auto-refresh timer, and a sequence number so a slow
  // response can't overwrite a newer one
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const requestSeqRef = useRef(0);

  const formatDeal = (d) => ({
    id: d._id,
    dealName: d.dealName,
    company: d.company?.name || "—",
    contactName: d.contact
      ? `${d.contact.firstName} ${d.contact.lastName}`
      : "—",
    stage: d.stage?._id || d.stage || "",
    stageName: d.stage?.name || "",
    amount: d.amount || 0,
    closingDate: d.closingDate,
    owner: ownerName,
    priority: d.priority?.toLowerCase() || "medium",
    description: d.description || "",
    source: d.source || "",
    mobile: d.mobile,
    createdAt: d.createdAt,
    email: d.email,
    interest: d.interest || "",
    subInterest: d.subInterest || "",
    disposition: d.disposition || "",
    subDisposition: d.subDisposition || "",
    followUpDate: d.followUpDate || null,
    assigneeName: d.assignee?.name || (d.assignee?.firstName ? `${d.assignee.firstName} ${d.assignee.lastName || ""}`.trim() : (typeof d.assignee === "string" ? d.assignee : "—")),
    // POC Fields
    pocIdentified: d.pocIdentified || "",
    pocName: d.pocName || "",
    pocPhone: d.pocPhone || "",
    pocEmail: d.pocEmail || "",
    pocComments: d.pocComments || "",
    // Complaint / Ticket Fields
    complaintDate: d.complaintDate || "",
    complaintTime: d.complaintTime || "",
    loggedBy: d.loggedBy || "",
    callerName: d.callerName || "",
    paymentType: d.paymentType || "",
    machineNumber: d.machineNumber || "",
    reasonForContact: d.reasonForContact || "",
    complaintType: d.complaintType || "",
    errorCode: d.errorCode || "",
    juicesPushed: d.juicesPushed || "",
    creditNoteJuices: d.creditNoteJuices || "",
    commandCode: d.commandCode || "",
    complaintStatus: d.complaintStatus || "",
    refundAmount: d.refundAmount || "",
    customerEmailAddress: d.customerEmailAddress || "",
    complaintComments: d.complaintComments || "",
    city: d.city || "",
    locationArea: d.locationArea || "",
    country: d.country || "",
    whatsappNumber: d.whatsappNumber || "",
    linkedinUrl: d.linkedinUrl || "",
    jobTitle: d.jobTitle || "",
    primaryIndustry: d.primaryIndustry || "",
    companyName: d.companyName || "",
    // Rider / Gazmin Fields
    riderId: d.riderId || "",
    riderName: d.riderName || "",
    riderPlatform: d.riderPlatform || "",
    bikeNo: d.bikeNo || "",
    visaNo: d.visaNo || "",
    currentStore: d.currentStore || "",
    previousStore: d.previousStore || "",
    riderStatus: d.riderStatus || "",
    // Tatweer Fields
    area: d.area || "",
    buildingName: d.buildingName || "",
    unitNo: d.unitNo || "",
    serviceCategory: d.serviceCategory || "",
    requestType: d.requestType || "",
    existingTicketId: d.existingTicketId || "",
    preferredDateTime: d.preferredDateTime || "",
    remarks: d.remarks || "",
    notes: d.notes || [],
  });

  const requestParams = (currentFilters) => {
    // datePreset is UI-only; startDate/endDate already carry the range
    const { datePreset, ...apiFilters } = currentFilters;
    return {
      role: localStorage.getItem("userRole"),
      memberId: localStorage.getItem("memberId"),
      pipeline,
      // Date filters are calendar days; the API resolves them in this timezone
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...apiFilters,
    };
  };

  // Reloads whatever the current view shows, with the current filters
  const fetchDeals = async (currentFilters = filtersRef.current) => {
    const seq = ++requestSeqRef.current;
    const params = requestParams(currentFilters);
    try {
      if (view === VIEW_TYPES.LIST) {
        const res = await dealsAPI.getAll(userId, {
          ...params,
          page: listPage + 1,
          limit: listRowsPerPage,
        });
        if (seq !== requestSeqRef.current) return;
        setList({ deals: res.data.deals.map(formatDeal), total: res.data.total });
      } else if (view === VIEW_TYPES.APPOINTMENTS) {
        const res = await dealsAPI.getAll(userId, { ...params, hasFollowUp: true });
        if (seq !== requestSeqRef.current) return;
        setFollowUps(res.data.map(formatDeal));
      } else {
        const res = await dealsAPI.getAll(userId, {
          ...params,
          view: "board",
          perStage: BOARD_PAGE_SIZE,
        });
        if (seq !== requestSeqRef.current) return;
        const columns = {};
        res.data.stages.forEach((col) => {
          columns[col.stageId] = {
            deals: col.deals.map(formatDeal),
            total: col.total,
            loadingMore: false,
          };
        });
        setBoard({ total: res.data.total, columns });
      }
    } catch (error) {
      console.error("Error fetching deals:", error);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (userId && loadDeals) fetchDeals(filters);
  }, [userId, userName, filters, pipeline, view, listPage, listRowsPerPage]);

  // Back to the first list page whenever the filters or pipeline change — but
  // not on mount, so a page restored from the URL is kept
  const resultSetKey = `${pipeline}:${JSON.stringify(filters)}`;
  const resultSetKeyRef = useRef(resultSetKey);
  useEffect(() => {
    if (resultSetKeyRef.current === resultSetKey) return;
    resultSetKeyRef.current = resultSetKey;
    if (listPage > 0) setListParams(0, listRowsPerPage);
  }, [resultSetKey]);

  const updateColumn = (stageId, patch) =>
    setBoard((prev) => ({
      ...prev,
      columns: {
        ...prev.columns,
        [stageId]: { ...prev.columns[stageId], ...patch(prev.columns[stageId]) },
      },
    }));

  // Board: append the next page of one stage column
  const loadMoreStage = async (stageId) => {
    const column = board.columns[stageId];
    if (!column || column.loadingMore || column.deals.length >= column.total) return;

    updateColumn(stageId, () => ({ loadingMore: true }));
    try {
      const res = await dealsAPI.getAll(userId, {
        ...requestParams(filtersRef.current),
        stage: stageId,
        offset: column.deals.length,
        limit: BOARD_PAGE_SIZE,
      });
      updateColumn(stageId, (col) => {
        const seen = new Set(col.deals.map((d) => d.id));
        const more = res.data.deals.map(formatDeal).filter((d) => !seen.has(d.id));
        return { deals: [...col.deals, ...more], total: res.data.total, loadingMore: false };
      });
    } catch (error) {
      console.error("Error loading more deals:", error);
      updateColumn(stageId, () => ({ loadingMore: false }));
    }
  };

  // Every matching lead (with notes), for export
  const fetchAllDeals = async () => {
    const res = await dealsAPI.getAll(userId, requestParams(filtersRef.current));
    return res.data.map(formatDeal);
  };

  // Leads currently loaded for the active view
  const deals = useMemo(() => {
    if (view === VIEW_TYPES.LIST) return list.deals;
    if (view === VIEW_TYPES.APPOINTMENTS) return followUps;
    return Object.values(board.columns).flatMap((col) => col.deals);
  }, [view, board, list, followUps]);

  const totalDeals =
    view === VIEW_TYPES.LIST
      ? list.total
      : view === VIEW_TYPES.APPOINTMENTS
      ? followUps.length
      : board.total;

  // Applies a change to one loaded deal on the board, moving it between columns
  // (and adjusting their counts) when its stage changes. `next` = null removes it.
  const updateDealInBoard = (prev, dealId, next) => {
    const columns = { ...prev.columns };
    let total = prev.total;
    let existing = null;
    for (const [stageId, col] of Object.entries(columns)) {
      const found = col.deals.find((d) => d.id === dealId);
      if (!found) continue;
      existing = found;
      columns[stageId] = {
        ...col,
        deals: col.deals.filter((d) => d.id !== dealId),
        total: col.total - 1,
      };
      total -= 1;
    }
    if (!existing || !next) return { total, columns };

    const merged = { ...existing, ...next };
    const target = columns[merged.stage];
    if (target) {
      columns[merged.stage] = {
        ...target,
        deals: [merged, ...target.deals],
        total: target.total + 1,
      };
      total += 1;
    }
    return { total, columns };
  };

  // Deal Operations
  const dealOperations = {
    create: async (payload) => {
      try {
        await dealsAPI.create({ ...payload, pipeline, userId });
        await fetchDeals();
        return { success: true };
      } catch (error) {
        console.error("Error creating deal:", error);
        return { success: false, error };
      }
    },

    update: async (dealId, payload) => {
      console.log(payload, "payload");
      try {
        const res = await dealsAPI.update(dealId, payload);
        const updated = res.data.deal;

        const formatted = {
          id: updated._id,
          _id: updated._id,
          dealName: updated.dealName,
          company: updated.company || null,
          contact: updated.contact || null,
          stage: updated.stage?._id || updated?.stage || "",
          stageName: updated.stage?.name || "",
          amount: updated.amount || 0,
          closingDate: updated.closingDate,
          priority: updated.priority?.toLowerCase() || "medium",
          description: updated.description || "",
          source: updated.source || "",
          mobile: updated.mobile || "",
          // POC Fields
          pocIdentified: updated.pocIdentified || "",
          pocName: updated.pocName || "",
          pocPhone: updated.pocPhone || "",
          pocEmail: updated.pocEmail || "",
          pocComments: updated.pocComments || "",
          // Complaint / Ticket Fields
          complaintDate: updated.complaintDate || "",
          complaintTime: updated.complaintTime || "",
          loggedBy: updated.loggedBy || "",
          callerName: updated.callerName || "",
          paymentType: updated.paymentType || "",
          machineNumber: updated.machineNumber || "",
          reasonForContact: updated.reasonForContact || "",
          complaintType: updated.complaintType || "",
          errorCode: updated.errorCode || "",
          juicesPushed: updated.juicesPushed || "",
          creditNoteJuices: updated.creditNoteJuices || "",
          commandCode: updated.commandCode || "",
          complaintStatus: updated.complaintStatus || "",
          refundAmount: updated.refundAmount || "",
          customerEmailAddress: updated.customerEmailAddress || "",
          complaintComments: updated.complaintComments || "",
          city: updated.city || "",
          locationArea: updated.locationArea || "",
          interest: updated.interest || "",
          subInterest: updated.subInterest || "",
          // Rider / Gazmin Fields
          riderId: updated.riderId || "",
          riderName: updated.riderName || "",
          riderPlatform: updated.riderPlatform || "",
          bikeNo: updated.bikeNo || "",
          visaNo: updated.visaNo || "",
          currentStore: updated.currentStore || "",
          previousStore: updated.previousStore || "",
          riderStatus: updated.riderStatus || "",
          // Tatweer Fields
          area: updated.area || "",
          buildingName: updated.buildingName || "",
          unitNo: updated.unitNo || "",
          serviceCategory: updated.serviceCategory || "",
          requestType: updated.requestType || "",
          existingTicketId: updated.existingTicketId || "",
          preferredDateTime: updated.preferredDateTime || "",
          remarks: updated.remarks || "",
        };
        console.log(formatted, "formatted");
        setBoard((prev) => updateDealInBoard(prev, dealId, formatted));
        setList((prev) => ({
          ...prev,
          deals: prev.deals.map((d) => (d.id === dealId ? { ...d, ...formatted } : d)),
        }));
        setFollowUps((prev) =>
          prev.map((d) => (d.id === dealId ? { ...d, ...formatted } : d))
        );
        return { success: true, deal: formatted };
      } catch (error) {
        console.error("Error updating deal:", error);
        return { success: false, error };
      }
    },

    delete: async (dealId) => {
      try {
        await dealsAPI.delete(dealId);
        setBoard((prev) => updateDealInBoard(prev, dealId, null));
        setFollowUps((prev) => prev.filter((d) => d.id !== dealId));
        // The list refills the page from the server
        if (view === VIEW_TYPES.LIST) await fetchDeals();
        return { success: true };
      } catch (error) {
        console.error("Error deleting deal:", error);
        return { success: false, error };
      }
    },

    move: async (dealId, targetStageId) => {
      const deal = deals.find((d) => d.id === dealId);
      if (!deal) return { success: false };

      const targetStage = stages.find(
        (s) => s._id === targetStageId || s.id === targetStageId
      );
      if (!targetStage) return { success: false };

      const previousBoard = board;

      // Optimistic update
      setBoard((prev) =>
        updateDealInBoard(prev, dealId, {
          stage: targetStage._id,
          stageName: targetStage.name,
        })
      );

      try {
        const res = await dealsAPI.update(dealId, { stage: targetStage._id });

        if (res.status === 200) {
          return { success: true };
        } else {
          throw new Error("Unexpected response");
        }
      } catch (error) {
        console.error("Error moving deal:", error);
        // Rollback
        setBoard(previousBoard);
        return { success: false, error };
      }
    },
  };

  // Stage Operations
  const stageOperations = {
    create: async (values) => {
      try {
        const res = await stagesAPI.create({ ...values, pipeline, userId });
        setStages((prev) => {
          const updated = [...prev, res.data.stage];
          return updated.sort((a, b) => (a.order || 0) - (b.order || 0));
        });
        return { success: true };
      } catch (error) {
        console.error("Error creating stage:", error);
        return { success: false, error };
      }
    },

    update: async (stageId, values) => {
      try {
        const res = await stagesAPI.update(stageId, values);
        setStages((prev) => {
          const updated = prev.map((s) => (s._id === stageId ? res.data.stage : s));
          return updated.sort((a, b) => (a.order || 0) - (b.order || 0));
        });
        return { success: true };
      } catch (error) {
        console.error("Error updating stage:", error);
        return { success: false, error };
      }
    },

    delete: async (stageId) => {
      try {
        const res = await stagesAPI.delete(stageId);
        setStages((prev) => prev.filter((s) => s._id !== stageId));
        return { success: true, message: res.data.message };
      } catch (error) {
        console.error("Error deleting stage:", error);
        return { success: false, error };
      }
    },
  };

  // Company Operations
  const companyOperations = {
    create: async (values) => {
      try {
        const res = await companiesAPI.create({ ...values, userId });
        setCompanies((prev) => [...prev, res.data.company]);
        return { success: true };
      } catch (error) {
        console.error("Error creating company:", error);
        return { success: false, error };
      }
    },
  };

  // Contact Operations
  const contactOperations = {
    create: async (payload) => {
      try {
        const res = await contactsAPI.create({ ...payload, userId });
        setContacts((prev) => [...prev, res.data.contact]);
        return { success: true };
      } catch (error) {
        console.error("Error creating contact:", error);
        return { success: false, error };
      }
    },
  };

  // Notes Operations
  const noteOperations = {
    add: (dealId, noteText) => {
      if (!noteText.trim()) return;
      const newNote = {
        id: String(Date.now()),
        dealId,
        text: noteText,
        author: userName,
        createdAt: new Date().toISOString(),
      };
      setNotes([...notes, newNote]);
      return { success: true };
    },

    delete: (noteId) => {
      setNotes(notes.filter((n) => n.id !== noteId));
      return { success: true };
    },

    getByDeal: (dealId) => {
      return notes
        .filter((n) => n.dealId === dealId)
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
  };

  return {
    deals,
    totalDeals,
    boardColumns: board.columns,
    loadMoreStage,
    fetchAllDeals,
    listPagination: {
      total: list.total,
      page: listPage,
      rowsPerPage: listRowsPerPage,
      onPageChange: (page) => setListParams(page, listRowsPerPage),
      onRowsPerPageChange: (rows) => setListParams(0, rows),
    },
    stages,
    companies,
    contacts,
    appointments,
    notes,
    loading,
    filters,
    setFilters,
    fetchDeals,
    dealOperations,
    stageOperations,
    companyOperations,
    contactOperations,
    noteOperations,
    pipeline,
    setPipeline,
  };
};
